import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { cn } from "@/lib/utils";

interface TourStep {
  id: string;
  title: string;
  description: string;
  target?: string; // CSS selector for the element to highlight
  position?: "top" | "bottom" | "left" | "right";
  action?: string; // Optional action button text
}

const tourSteps: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to Step! 👋",
    description: "Let's take a quick tour to help you get started with tracking your wellbeing and growth.",
  },
  {
    id: "dashboard",
    title: "Your Personal Dashboard",
    description: "This is your command center. Here you can see your progress, insights, and quick actions all in one place.",
    target: ".dashboard-overview",
  },
  {
    id: "checkin",
    title: "Daily Check-ins",
    description: "Start each day with a quick check-in to track your mood, energy, and reflect on your progress. It only takes 2 minutes!",
    target: ".quick-checkin-card",
    action: "Try it now",
  },
  {
    id: "insights",
    title: "AI-Powered Insights",
    description: "After a few check-ins, you'll unlock personalized insights about your productivity patterns and wellbeing trends.",
    target: ".ai-insights-widget",
  },
  {
    id: "reflection",
    title: "Daily Reflections",
    description: "Capture your thoughts and build self-awareness with our reflection journal. Your entries are private and secure.",
    target: ".daily-reflection-card",
  },
  {
    id: "goals",
    title: "Track Your Growth",
    description: "Set and track personal goals. Visit your Profile page to manage your growth journey and skill development.",
    target: 'a[href="/profile"]',
    position: "bottom",
  },
  {
    id: "team",
    title: "Collaboration (Optional)",
    description: "When you're ready, you can join a team to unlock collaboration features. For now, enjoy your personal growth journey!",
    target: 'a[href="/team"]',
    position: "bottom",
  },
  {
    id: "complete",
    title: "You're All Set! 🎉",
    description: "That's the basics! Remember, consistency is key. Try to check in daily to get the most out of Step.",
  },
];

export default function OnboardingTour() {
  const { user } = useUser();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);

  // Update highlight rect when element changes or on scroll/resize
  const updateHighlightRect = (element: HTMLElement | null) => {
    if (element) {
      const rect = element.getBoundingClientRect();
      setHighlightRect(rect);
    } else {
      setHighlightRect(null);
    }
  };

  useEffect(() => {
    // Check if user has seen the tour
    const hasSeenTour = localStorage.getItem(`hasSeenTour_${user?.id}`);
    if (user?.isNewUser && !hasSeenTour) {
      // Start tour after a short delay
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, [user]);

  useEffect(() => {
    // Update highlighted element when step changes
    const step = tourSteps[currentStep];
    if (step.target) {
      const element = document.querySelector(step.target) as HTMLElement;
      if (element) {
        setHighlightedElement(element);
        // Scroll element into view
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        // Update rect after scroll animation completes
        setTimeout(() => updateHighlightRect(element), 350);
      }
    } else {
      setHighlightedElement(null);
      setHighlightRect(null);
    }
  }, [currentStep]);

  // Update highlight rect on scroll/resize
  useEffect(() => {
    if (!highlightedElement) return;

    const handleUpdate = () => updateHighlightRect(highlightedElement);
    
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [highlightedElement]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (confirm("Are you sure you want to skip the tour? You can always access it later from the help menu.")) {
      handleComplete();
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    if (user?.id) {
      localStorage.setItem(`hasSeenTour_${user.id}`, "true");
    }
  };

  const handleAction = () => {
    const step = tourSteps[currentStep];
    if (step.id === "checkin") {
      // Click the check-in button
      const checkinButton = document.querySelector(".quick-checkin-card button") as HTMLButtonElement;
      if (checkinButton) {
        checkinButton.click();
        handleComplete();
      }
    }
  };

  if (!isVisible) return null;

  const currentTourStep = tourSteps[currentStep];
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[100] animate-in fade-in duration-300" />

      {/* Highlight */}
      {highlightRect && (
        <div
          className="fixed z-[101] ring-4 ring-primary ring-offset-4 rounded-lg transition-all duration-300 pointer-events-none"
          style={{
            top: highlightRect.top - 4,
            left: highlightRect.left - 4,
            width: highlightRect.width + 8,
            height: highlightRect.height + 8,
          }}
        />
      )}

      {/* Tour Card */}
      <div className="fixed inset-0 flex items-center justify-center z-[102] p-4">
        <Card className="w-full max-w-md animate-in zoom-in-90 duration-300">
          <CardHeader className="relative pb-2">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2"
              onClick={handleSkip}
            >
              <X className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-primary/10 rounded-full">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-xl">{currentTourStep.title}</CardTitle>
            </div>
            <CardDescription className="text-base">
              {currentTourStep.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress bar */}
            <div className="w-full bg-secondary rounded-full h-2">
              <div
                className="bg-primary rounded-full h-2 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Step counter */}
            <div className="text-center text-sm text-muted-foreground">
              Step {currentStep + 1} of {tourSteps.length}
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>

              <div className="flex gap-2">
                {currentTourStep.action && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAction}
                  >
                    {currentTourStep.action}
                  </Button>
                )}
                
                <Button
                  variant={currentStep === tourSteps.length - 1 ? "default" : "outline"}
                  size="sm"
                  onClick={handleNext}
                >
                  {currentStep === tourSteps.length - 1 ? "Get Started" : "Next"}
                  {currentStep < tourSteps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}